import { ReportRowVm } from '../models/report-row-vm';
import { isRecord, parseReportJson } from './report-json.utils';
import { getRealTyallsRowId } from './report-row-id.utils';

const EXCLUDED_SUPPORTING_FILE_ROW_TYPES = new Set<string>([
   'groupHeader',
   'groupTotal',
   'sectionHeader',
   'sectionTotal',
   'summary',
   'summaryTotal',
   'grandTotal',
   'cashierTotal',
   'cashSummary',
   'paymentSummary',
   'userAdjustmentSummary',
   'userRefundSummary'
]);

/**
 * Collect genuine TyallsRowIds from the generated PMS report.
 * Synthetic UI row IDs are never added to this collection.
 */
export function getPersistedPmsReportRowIds(
   reportData: unknown
): ReadonlySet<string> {
   const rowIds = new Set<string>();
   const pending: unknown[] = [parseReportJson(reportData)];

   while (pending.length > 0) {
      const value = pending.pop();

      if (Array.isArray(value)) {
         for (const item of value) {
            if (item !== null && typeof item === 'object') {
               pending.push(item);
            }
         }

         continue;
      }

      if (!isRecord(value)) {
         continue;
      }

      const rowId = getRealTyallsRowId(value).trim();

      if (rowId && rowId !== '__REPORT__' && rowId.length <= 36) {
         rowIds.add(rowId);
      }

      for (const child of Object.values(value)) {
         if (child !== null && typeof child === 'object') {
            pending.push(child);
         }
      }
   }

   return rowIds;
}

/**
 * Supporting documents belong only to genuine one-to-one PMS rows.
 * Grouped, calculated and fallback-only rows are not eligible.
 */
export function isReportRowSupportingFileEligible(
   row: ReportRowVm,
   persistedRowIds: ReadonlySet<string>
): boolean {
   const rowId = row?.rowId?.trim();
   const rowType = String(row?.cells?.['rowType'] ?? '');
   const rowClass = String(row?.rowClass ?? '');

   return (
      !!rowId &&
      rowId !== '__REPORT__' &&
      row.sourceRowIds === undefined &&
      !EXCLUDED_SUPPORTING_FILE_ROW_TYPES.has(rowType) &&
      !rowClass.includes('report-group-header-row') &&
      !rowClass.includes('report-group-total-row') &&
      persistedRowIds.has(rowId)
   );
}
